import { getChatDialogListQuery } from './filters';
import { create } from 'zustand';
import {
    ApiChatReadState,
    ApiChatMessageSendStatus,
    type ChatDialogResponse,
    type ChatRealtimeEventResponse,
} from '@/core/generated/api';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';
import { chatDialogService } from '../api/chatDialogService';
import { loadChatDialogSnapshot } from '../api/chatDialogSnapshot';
import {
    getChatDialogReadBoundary,
    markAcknowledgedChatDialogMessagesRead,
    mergeChatDialogs,
    mergeChatDialogMessages,
} from './selectors';

import type {
    ChatDialogTransport,
    ChatDialogData,
    ChatDialogState,
    ChatDialogRefreshQuery,
    ChatDialogRealtimeVersions,
} from './types';

import { applyChatDialogRealtimeEvent } from './realtime';

const PAGE_SIZE = 30;

const initialData: ChatDialogData = {
    dialogs: [],
    totalCount: null,
    filter: 'all',
    searchFilter: {
        appUserIds: [],
        filterPeople: false,
        channels: [],
        petName: ''
    },
    matchedAppUserIds: null,
    nextOffset: null,
    selectedDialog: null,
    messages: [],
    nextBeforeSequence: null,
    nextAfterSequence: null,
    counters: null,
    listStatus: RequestStatus.Idle,
    listMoreStatus: RequestStatus.Idle,
    messagesStatus: RequestStatus.Idle,
    historyStatus: RequestStatus.Idle,
    countersStatus: RequestStatus.Idle,
    readStatus: RequestStatus.Idle,
    listError: null,
    listMoreError: null,
    messagesError: null,
    historyError: null,
    countersError: null,
    readError: null,
    lastReadSequence: 0,
    refreshStatus: RequestStatus.Idle,
    refreshError: null,
    realtimeEnabled: false,
};

/** Создаёт рабочее пространство переписки с пользователями */
export const createChatDialogStore = (service: ChatDialogTransport = chatDialogService) => {
    let listRequest = 0;
    let messagesRequest = 0;
    let countersRequest = 0;
    let readRequest = 0;
    let epoch = 0;
    let liveRequest: Promise<void> | null = null;
    let liveDirty = false;
    let pendingQuery: ChatDialogRefreshQuery = {};
    let snapshotReady = false;
    const bufferedEvents = new Map<string, ChatRealtimeEventResponse>();
    const companionRequests = new Set<string>();

    const versions: ChatDialogRealtimeVersions = {
        baseline: 0n,
        counters: 0n,
        dialogs: new Map(),
        messages: new Map(),
    };

    return create<ChatDialogState>((set, get) => {
        /** Загружает клички при первом появлении связанного собеседника */
        const loadCompanionPets = async (dialogId: string): Promise<void> => {
            const dialog = get().dialogs.find((item) => item.id === dialogId)
                ?? (get().selectedDialog?.id === dialogId
                    ? get().selectedDialog
                    : null);

            if (!dialog?.companionAppUserId || dialog.companionPetNames !== undefined) return;
            const key = `${epoch}:${dialogId}:${dialog.companionAppUserId}`;

            if (companionRequests.has(key)) return;
            companionRequests.add(key);
            const currentEpoch = epoch;

            try {
                const response = await service.getDialogs({
                    dialogId,
                    offset: 0,
                    limit: 1
                });

                const metadata = response.items?.find((item) => item.id === dialogId);

                if (currentEpoch !== epoch || !metadata) return;

                /** Дополняет клички, сохраняя текущее состояние переписки */
                const enrich = (current: ChatDialogResponse): ChatDialogResponse => (
                    current.id === dialogId && current.companionId === metadata.companionId
                        && current.companionAppUserId === metadata.companionAppUserId
                        ? {
                            ...current,
                            companionPetNames: metadata.companionPetNames ?? []
                        }
                        : current
                );

                set((state) => ({
                    dialogs: state.dialogs.map(enrich),
                    selectedDialog: state.selectedDialog
                        ? enrich(state.selectedDialog)
                        : null,
                }));
            } catch {
                companionRequests.delete(key);
            }
        };

        /** Применяет изменения, полученные во время восстановления */
        const flushBufferedEvents = (): void => {
            const events = [...bufferedEvents.values()];
            bufferedEvents.clear();

            events.forEach((event) => {
                const patch = applyChatDialogRealtimeEvent(get(), event, versions);

                if (patch.selectedDialog === null) get().clearSelection();
                set(patch);

                if (event.dialogId) void loadCompanionPets(event.dialogId);
            });
        };

        return {
            ...initialData,

            setSearchFilter: (searchFilter) => {
                listRequest++;
                snapshotReady = false;
                bufferedEvents.clear();
                get().clearSelection();

                set({
                    searchFilter,
                    dialogs: [],
                    totalCount: null,
                    nextOffset: null,
                    matchedAppUserIds: null
                });

                void get().fetchDialogs();
            },

            fetchDialogs: async () => {
                if (get().realtimeEnabled) {
                    set({
                        listStatus: RequestStatus.Loading,
                        listError: null
                    });

                    return get().refreshLive({ dialogLimit: PAGE_SIZE });
                }

                const requestId = ++listRequest;

                set({
                    listStatus: RequestStatus.Loading,
                    listError: null,
                    listMoreStatus: RequestStatus.Idle,
                    listMoreError: null,
                });

                try {
                    const response = await service.getDialogs({
                        ...getChatDialogListQuery(get()),
                        offset: 0,
                        limit: PAGE_SIZE
                    });

                    if (requestId !== listRequest) return;

                    const dialogs = mergeChatDialogs([], response.items ?? []);

                    const selectedDialog = dialogs.find((dialog) => dialog.id === get().selectedDialog?.id)
                        ?? get().selectedDialog;

                    set({
                        dialogs,
                        matchedAppUserIds: response.matchedAppUserIds ?? null,
                        selectedDialog,
                        totalCount: response.totalCount ?? null,
                        nextOffset: response.nextOffset ?? null
                    });
                } catch (error: unknown) {
                    if (requestId !== listRequest) return;
                    set({ listError: getErrorMessage(error, 'Не удалось загрузить диалоги') });
                } finally {
                    if (requestId === listRequest) {
                        set({
                            listStatus: get().listError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            fetchMoreDialogs: async () => {
                const state = get();

                const unavailable = state.nextOffset === null || state.listStatus !== RequestStatus.Success
                    || state.listMoreStatus === RequestStatus.Loading;

                if (unavailable) return;
                const requestId = listRequest;

                set({
                    listMoreStatus: RequestStatus.Loading,
                    listMoreError: null
                });

                if (state.realtimeEnabled) {
                    await get().refreshLive({ dialogLimit: state.dialogs.length + PAGE_SIZE });

                    return;
                }

                try {
                    const response = await service.getDialogs({
                        ...getChatDialogListQuery(state),
                        offset: state.nextOffset ?? 0,
                        limit: PAGE_SIZE,
                    });

                    if (requestId !== listRequest) return;

                    set({
                        dialogs: mergeChatDialogs(get().dialogs, response.items ?? []),
                        totalCount: response.totalCount ?? null,
                        nextOffset: response.nextOffset ?? null,
                    });
                } catch (error: unknown) {
                    if (requestId !== listRequest) return;
                    set({ listMoreError: getErrorMessage(error, 'Не удалось загрузить ещё диалоги') });
                } finally {
                    if (requestId === listRequest) {
                        set({
                            listMoreStatus: get().listMoreError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            setFilter: (filter) => {
                if (filter === get().filter) return;

                set({
                    filter,
                    dialogs: [],
                    totalCount: null,
                    nextOffset: null
                });

                void get().fetchDialogs();
            },

            selectDialog: (dialog) => {
                if (!dialog.id || dialog.id === get().selectedDialog?.id) return;
                get().clearSelection();
                set({ selectedDialog: dialog });
                void get().fetchMessages();
            },

            clearSelection: () => {
                messagesRequest += 1;
                readRequest += 1;

                set({
                    selectedDialog: null,
                    messages: [],
                    nextBeforeSequence: null,
                    nextAfterSequence: null,
                    messagesStatus: RequestStatus.Idle,
                    historyStatus: RequestStatus.Idle,
                    readStatus: RequestStatus.Idle,
                    messagesError: null,
                    historyError: null,
                    readError: null,
                    lastReadSequence: 0,
                });
            },

            fetchMessages: async () => {
                const dialogId = get().selectedDialog?.id;

                if (!dialogId) return;
                const requestId = ++messagesRequest;
                readRequest += 1;

                set({
                    messages: [],
                    nextBeforeSequence: null,
                    nextAfterSequence: null,
                    messagesStatus: RequestStatus.Loading,
                    historyStatus: RequestStatus.Idle,
                    readStatus: RequestStatus.Idle,
                    messagesError: null,
                    historyError: null,
                    readError: null,
                });

                if (get().realtimeEnabled) {
                    await get().refreshLive();

                    return;
                }

                try {
                    if (!get().selectedDialog?.companionId) {
                        const page = await service.getDialogs({
                            dialogId,
                            offset: 0,
                            limit: 1,
                        });

                        if (requestId !== messagesRequest) return;

                        const dialog = page.items?.find((item) => item.id === dialogId);

                        if (!dialog) throw new Error('Диалог недоступен или не найден');

                        set({ selectedDialog: dialog });
                    }

                    const response = await service.getMessages({
                        dialogId,
                        limit: PAGE_SIZE
                    });

                    if (requestId !== messagesRequest) return;

                    set({
                        messages: markAcknowledgedChatDialogMessagesRead(
                            mergeChatDialogMessages(response.items ?? [], get().messages),
                            get().lastReadSequence,
                        ),
                        nextBeforeSequence: response.nextBeforeSequence ?? null,
                        nextAfterSequence: response.nextAfterSequence ?? null,
                    });
                } catch (error: unknown) {
                    if (requestId !== messagesRequest) return;
                    set({ messagesError: getErrorMessage(error, 'Не удалось загрузить историю') });
                } finally {
                    if (requestId === messagesRequest) {
                        set({
                            messagesStatus: get().messagesError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            fetchOlderMessages: async () => {
                const state = get();
                const dialogId = state.selectedDialog?.id;

                if (!dialogId) return;

                const unavailable = state.nextBeforeSequence === null
                    || state.messagesStatus !== RequestStatus.Success || state.historyStatus === RequestStatus.Loading
                    || state.refreshStatus === RequestStatus.Loading;

                if (unavailable) return;
                const requestId = messagesRequest;

                set({
                    historyStatus: RequestStatus.Loading,
                    historyError: null
                });

                if (state.realtimeEnabled) {
                    await get().refreshLive({ beforeSequence: state.nextBeforeSequence ?? undefined });

                    return;
                }

                try {
                    const response = await service.getMessages({
                        dialogId,
                        beforeSequence: state.nextBeforeSequence ?? undefined,
                        limit: PAGE_SIZE,
                    });

                    if (requestId !== messagesRequest) return;

                    set({
                        messages: markAcknowledgedChatDialogMessagesRead(
                            mergeChatDialogMessages(response.items ?? [], get().messages),
                            get().lastReadSequence,
                        ),
                        nextBeforeSequence: response.nextBeforeSequence ?? null,
                    });
                } catch (error: unknown) {
                    if (requestId !== messagesRequest) return;
                    set({ historyError: getErrorMessage(error, 'Не удалось загрузить старые сообщения') });
                } finally {
                    if (requestId === messagesRequest) {
                        set({
                            historyStatus: get().historyError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            fetchNewerMessages: async () => {
                const state = get();
                const dialogId = state.selectedDialog?.id;

                const unavailable = !dialogId || state.nextAfterSequence === null
                    || state.historyStatus === RequestStatus.Loading
                    || state.messagesStatus !== RequestStatus.Success
                    || state.refreshStatus === RequestStatus.Loading;

                if (unavailable || !dialogId) return;

                const requestId = messagesRequest;

                set({
                    historyStatus: RequestStatus.Loading,
                    historyError: null
                });

                if (state.realtimeEnabled) {
                    await get().refreshLive({ afterSequence: state.nextAfterSequence ?? undefined });

                    return;
                }

                try {
                    const response = await service.getMessages({
                        dialogId,
                        afterSequence: state.nextAfterSequence ?? undefined,
                        limit: PAGE_SIZE,
                    });

                    if (requestId !== messagesRequest) return;

                    set({
                        messages: markAcknowledgedChatDialogMessagesRead(
                            mergeChatDialogMessages(get().messages, response.items ?? []),
                            get().lastReadSequence,
                        ),
                        nextAfterSequence: response.nextAfterSequence ?? null,
                    });
                } catch (error: unknown) {
                    if (requestId !== messagesRequest) return;
                    set({ historyError: getErrorMessage(error, 'Не удалось загрузить следующие сообщения') });
                } finally {
                    if (requestId === messagesRequest) {
                        set({
                            historyStatus: get().historyError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            fetchCounters: async () => {
                if (get().realtimeEnabled) return get().refreshLive();
                const requestId = ++countersRequest;

                set({
                    countersStatus: RequestStatus.Loading,
                    countersError: null
                });

                try {
                    const counters = await service.getCounters();

                    if (requestId !== countersRequest) return;
                    set({ counters });
                } catch (error: unknown) {
                    if (requestId !== countersRequest) return;
                    set({ countersError: getErrorMessage(error, 'Не удалось загрузить счётчики') });
                } finally {
                    if (requestId === countersRequest) {
                        set({
                            countersStatus: get().countersError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            markRead: async (throughMessageId) => {
                const state = get();
                const dialogId = state.selectedDialog?.id;

                const boundary = getChatDialogReadBoundary(
                    state.messages.filter((message) => message.id === throughMessageId),
                );

                if (!dialogId || !boundary) return;

                const unavailable = state.readStatus === RequestStatus.Loading
                    || state.messagesStatus !== RequestStatus.Success
                    || boundary.sequence <= state.lastReadSequence && !state.selectedDialog?.isManuallyUnread;

                if (unavailable) return;
                const requestId = ++readRequest;
                const currentEpoch = epoch;

                set({
                    readStatus: RequestStatus.Loading,
                    readError: null
                });

                try {
                    await service.markRead(dialogId, boundary.messageId);

                    if (currentEpoch !== epoch) return;

                    if (requestId === readRequest) {
                        const lastReadSequence = Math.max(get().lastReadSequence, boundary.sequence);

                        set({
                            lastReadSequence,
                            selectedDialog: get().selectedDialog
                                ? {
                                    ...get().selectedDialog,
                                    isManuallyUnread: false
                                }
                                : null,
                            messages: markAcknowledgedChatDialogMessagesRead(get().messages, lastReadSequence),
                        });
                    }

                    if (!get().realtimeEnabled) {
                        await Promise.all([get().fetchDialogs(), get().fetchCounters()]);
                    }
                } catch (error: unknown) {
                    if (requestId !== readRequest) return;
                    set({ readError: getErrorMessage(error, 'Не удалось подтвердить прочтение') });
                } finally {
                    if (requestId === readRequest) {
                        set({
                            readStatus: get().readError
                                ? RequestStatus.Error
                                : RequestStatus.Success
                        });
                    }
                }
            },

            sendMessage: async (dialogId, request) => {
                const currentEpoch = epoch;

                const message = await service.sendMessage({
                    dialogId,
                    sendChatDialogMessageRequest: request,
                });

                if (currentEpoch !== epoch) return message;

                if (get().selectedDialog?.id === dialogId) {
                    const existing = get().messages.find((item) => item.id === message.id);
                    const status = existing?.sendStatus?.id;
                    const alreadyCompleted = status !== undefined && status !== ApiChatMessageSendStatus.Pending;

                    if (!alreadyCompleted && (get().nextAfterSequence === null || existing)) {
                        set({ messages: mergeChatDialogMessages(get().messages, [message]) });
                    }
                }

                if (!get().realtimeEnabled) {
                    await Promise.all([get().fetchDialogs(), get().fetchCounters()]);
                }

                return message;
            },

            retryMessage: async (dialogId, messageId, expectedAttemptId) => {
                const currentEpoch = epoch;

                const message = await service.retryMessage({
                    dialogId,
                    messageId,
                    retryChatDialogMessageRequest: { expectedAttemptId },
                });

                if (currentEpoch !== epoch || get().selectedDialog?.id !== dialogId) return;

                set({
                    messages: get().messages.map((existing) => existing.id === messageId
                        && existing.retryAttemptId === expectedAttemptId
                        ? {
                            ...message,
                            readState: existing.readState?.id === ApiChatReadState.Read
                                ? existing.readState
                                : message.readState
                        }
                        : existing),
                });

                await get().refreshLive();
            },

            refreshLive: (query) => {
                liveDirty = true;
                pendingQuery = {
                    ...pendingQuery,
                    ...query
                };

                if (liveRequest) return liveRequest;
                const currentEpoch = epoch;

                /** Восстанавливает согласованное состояние рабочего пространства */
                const refresh = async (): Promise<void> => {
                    try {
                        while (liveDirty && currentEpoch === epoch) {
                            liveDirty = false;
                            const options = pendingQuery;
                            pendingQuery = {};
                            const state = get();
                            const listId = ++listRequest;
                            const messageId = ++messagesRequest;
                            const countersId = ++countersRequest;
                            const dialogId = state.selectedDialog?.id;

                            set({
                                refreshStatus: RequestStatus.Loading,
                                refreshError: null
                            });

                            /** Проверяет актуальность открытой переписки */
                            const isCurrent = (): boolean => currentEpoch === epoch
                                && state.filter === get().filter && state.searchFilter === get().searchFilter
                                && dialogId === get().selectedDialog?.id
                                && state.realtimeEnabled === get().realtimeEnabled
                                && listId === listRequest && messageId === messagesRequest
                                && countersId === countersRequest;

                            try {
                                if (state.realtimeEnabled) {
                                    const snapshot = await service.getSnapshot({
                                        ...getChatDialogListQuery(state),
                                        dialogLimit: options.dialogLimit ?? Math.max(PAGE_SIZE, state.dialogs.length),
                                        dialogId,
                                        oldestSequence: state.messages[0]?.sequence,
                                        newestSequence: state.nextAfterSequence !== null
                                            ? state.messages[state.messages.length - 1]?.sequence
                                            : undefined,
                                        beforeSequence: options.beforeSequence,
                                        afterSequence: options.afterSequence,
                                    });

                                    if (!isCurrent()) {
                                        if (currentEpoch === epoch) liveDirty = true;
                                        continue;
                                    }

                                    const revision = BigInt(snapshot.revision);

                                    versions.baseline = revision;
                                    versions.counters = revision;
                                    versions.dialogs.clear();
                                    versions.messages.clear();

                                    snapshotReady = true;

                                    const lastReadSequence = Math.max(
                                        get().lastReadSequence,
                                        snapshot.readThroughSequence ?? 0,
                                    );

                                    set({
                                        dialogs: snapshot.dialogs.items ?? [],
                                        matchedAppUserIds: snapshot.dialogs.matchedAppUserIds ?? null,
                                        totalCount: snapshot.dialogs.totalCount ?? null,
                                        nextOffset: snapshot.dialogs.nextOffset ?? null,
                                        selectedDialog: snapshot.selectedDialog ?? null,
                                        counters: snapshot.counters,
                                        listStatus: RequestStatus.Success,
                                        countersStatus: RequestStatus.Success,
                                        listMoreStatus: RequestStatus.Idle,
                                        listError: null,
                                        listMoreError: null,
                                        countersError: null,
                                        ...(dialogId
                                            ? {
                                                messages: markAcknowledgedChatDialogMessagesRead(
                                                    snapshot.history?.items ?? [],
                                                    lastReadSequence,
                                                ),
                                                lastReadSequence,
                                                nextBeforeSequence: snapshot.history?.nextBeforeSequence ?? null,
                                                nextAfterSequence: snapshot.history?.nextAfterSequence ?? null,
                                                messagesStatus: RequestStatus.Success,
                                                historyStatus: RequestStatus.Idle,
                                                messagesError: null,
                                                historyError: null,
                                            }
                                            : {}),
                                    });

                                    flushBufferedEvents();
                                } else {
                                    const snapshot = await loadChatDialogSnapshot(state, isCurrent, service);

                                    if (!snapshot) {
                                        if (currentEpoch === epoch) liveDirty = true;
                                        continue;
                                    }

                                    set({
                                        dialogs: snapshot.dialogs,
                                        matchedAppUserIds: snapshot.matchedAppUserIds,
                                        totalCount: snapshot.totalCount ?? null,
                                        nextOffset: snapshot.nextOffset,
                                        selectedDialog: snapshot.dialogs.find((dialog) => dialog.id === dialogId)
                                            ?? get().selectedDialog,
                                        counters: snapshot.counters,
                                        listStatus: RequestStatus.Success,
                                        listMoreStatus: RequestStatus.Idle,
                                        countersStatus: RequestStatus.Success,
                                        listError: null,
                                        listMoreError: null,
                                        countersError: null,
                                        ...(snapshot.messages === null
                                            ? {}
                                            : {
                                                messages: markAcknowledgedChatDialogMessagesRead(
                                                    mergeChatDialogMessages(get().messages, snapshot.messages),
                                                    get().lastReadSequence,
                                                ),
                                                nextBeforeSequence: snapshot.nextBeforeSequence,
                                                nextAfterSequence: snapshot.nextAfterSequence,
                                                messagesStatus: RequestStatus.Success,
                                                historyStatus: RequestStatus.Idle,
                                                messagesError: null,
                                                historyError: null,
                                            }),
                                    });
                                }
                            } catch (error: unknown) {
                                if (!isCurrent()) {
                                    if (currentEpoch === epoch) liveDirty = true;
                                    continue;
                                }

                                const message = getErrorMessage(error, 'Не удалось обновить переписку');

                                set({
                                    refreshError: message,
                                    ...(get().listStatus === RequestStatus.Loading
                                        ? {
                                            listStatus: RequestStatus.Error,
                                            listError: message,
                                        }
                                        : {}),
                                    ...(get().countersStatus === RequestStatus.Loading
                                        ? {
                                            countersStatus: RequestStatus.Error,
                                            countersError: message,
                                        }
                                        : {}),
                                    ...(get().messagesStatus === RequestStatus.Loading
                                        ? {
                                            messagesStatus: RequestStatus.Error,
                                            messagesError: message,
                                        }
                                        : {}),
                                    listMoreStatus: RequestStatus.Idle,
                                    historyStatus: RequestStatus.Idle,
                                });

                                liveDirty = false;
                            }
                        }
                    } finally {
                        if (currentEpoch === epoch) {
                            liveRequest = null;

                            if (snapshotReady) flushBufferedEvents();

                            set({
                                refreshStatus: get().refreshError
                                    ? RequestStatus.Error
                                    : RequestStatus.Success
                            });
                        }
                    }
                };

                liveRequest = refresh();

                return liveRequest;
            },

            applyRealtime: (event) => {
                if (!get().realtimeEnabled) return;

                if (liveRequest || !snapshotReady) {
                    const key = event.dialogId + ':' + (event.message?.id ?? event.removedMessageId ?? 'dialog');
                    const previous = bufferedEvents.get(key);

                    if (!previous || BigInt(event.revision) >= BigInt(previous.revision)) {
                        bufferedEvents.set(key, event);
                    }

                    if (get().searchFilter.companionName?.trim()) liveDirty = true;
                    return;
                }

                const patch = applyChatDialogRealtimeEvent(get(), event, versions);

                if (patch.selectedDialog === null) get().clearSelection();
                set(patch);

                if (event.dialogId) void loadCompanionPets(event.dialogId);
                if (get().searchFilter.companionName?.trim()) void get().refreshLive();
            },

            setRealtimeEnabled: (enabled) => {
                if (get().realtimeEnabled === enabled) return;
                listRequest += 1;
                messagesRequest += 1;
                countersRequest += 1;
                snapshotReady = false;
                bufferedEvents.clear();
                set({ realtimeEnabled: enabled });
            },

            reset: () => {
                epoch += 1;
                companionRequests.clear();
                liveRequest = null;
                liveDirty = false;
                snapshotReady = false;
                pendingQuery = {};
                bufferedEvents.clear();
                listRequest += 1;
                messagesRequest += 1;
                countersRequest += 1;
                readRequest += 1;
                set(initialData);
            },
        };
    });
};

/** Хранит диалоги и показанную переписку */
export const useChatDialogStore = createChatDialogStore();
